import { useState, useContext, useEffect, useRef } from "react";
import { CheckCircle, XCircle, MinusCircle, FileText, PencilLine, CloudDownload, Users, Download, Package, Calendar, User, Award, House, Mailbox, MapPin, IdCard, MapPinned, Locate, SquareUserRound, Mail, Phone, AtSign} from "lucide-react";
import { Link } from "react-router-dom";
import { HiClipboardDocumentList } from "react-icons/hi2";
import { MdHealthAndSafety } from "react-icons/md";
import { IoIosWarning } from "react-icons/io";
import { abrirArchivo } from "../../../../../providers/documentos";
import BreadCrumb from "../../../../../components/navbar/Breadcrumb";
import PrevNextNav from "../../../../../components/navbar/PrevNextNav";
import { UsuarioContext } from "../../../../../providers/userContext";
import { cambiarFormatoNombre, estadoServicio, formatoFechas, formatoFechasEs } from "../../../../../functions/funciones";
import { getSales, getVisitSheet, getInstalaciones } from "../../../../../services";
import { descargarCertificadoKae } from "../../../../../functions/pdfs";
import { riesgosData } from "../../../../../components/aaff/riesgos";
import Enlace from "../../../../../components/Enlace";
import { ExportToExcel } from "../../../../../providers/export-excel";

// ─── Badge ────────────────────────────────────────────────────────────────────
const Badge = ({ label, color = "green" }) => {
  const colors = {
    green: "bg-emerald-100 text-emerald-700",
    yellow: "bg-yellow-100 text-yellow-700",
    red: "bg-red-100 text-red-700",
    gray: "bg-gray-100 text-gray-500",
  };
  return (
    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${colors[color]}`}>
      {label}
    </span>
  );
};

// ─── Tab button ───────────────────────────────────────────────────────────────
const TabButton = ({ label, active, onClick }) => (
  <button
    onClick={onClick}
    className={`relative py-3 px-3 text-base transition-colors ${
      active
        ? "text-[#92004D] font-semibold  bg-[#ffeef7] rounded-lg"
        : "font-medium text-gray-400 hover:text-[#D37DD5]"
    }`}
  >
    {label}
    {active && (
      <span
        aria-hidden
        // className="absolute inset-x-0 -bottom-px h-[3px] rounded-t-sm bg-[#92004D]"
      />
    )}
  </button>
);

// ─── Stat box ─────────────────────────────────────────────────────────────────
const StatBox = ({ label, value }) => (
  <div className="flex-1 border border-gray-200 rounded-lg px-4 py-3 bg-white">
    <p className="text-[12px] text-gray-900 mb-1">{label}</p>
    <p className="text-sm font-semibold text-gray-800">{value}</p>
  </div>
);

// ─── Pending visit ────────────────────────────────────────────────────────────
const PendingVisit = () => (
  <div className="flex flex-col items-center justify-center py-10 text-center">
    <svg className="w-9 h-9 text-gray-300 mb-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path strokeLinecap="round" strokeLinejoin="round" d="M20 7H4a2 2 0 00-2 2v10a2 2 0 002 2h16a2 2 0 002-2V9a2 2 0 00-2-2zm0 0V5a2 2 0 00-2-2H6a2 2 0 00-2 2v2" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 12v4m0 0l-2-2m2 2l2-2" />
    </svg>
    <p className="text-sm font-medium text-gray-500">Pendiente de visita técnica</p>
    <p className="text-xs text-gray-400 mt-1 max-w-xs leading-relaxed">
      Los documentos de la visita aparecerán automáticamente en esta sección una vez finalizada la revisión técnica
    </p>
  </div>
);

// ─── Circular progress ────────────────────────────────────────────────────────
const CircleProgress = ({ pct, color = "#d1d5db" }) => {
  const r = 8;
  const circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" className="flex-shrink-0">
      <circle cx="10" cy="10" r={r} fill="none" stroke="#e5e7eb" strokeWidth="2.5" />
      {pct > 0 && (
        <circle
          cx="10" cy="10" r={r} fill="none"
          stroke={color} strokeWidth="2.5"
          strokeDasharray={`${dash} ${circ}`}
          strokeLinecap="round"
          transform="rotate(-90 10 10)"
        />
      )}
    </svg>
  );
};

const ToolboxIcon = ({ size = 20, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 512 512" className={className} xmlns="http://www.w3.org/2000/svg">
    <g fill="currentColor">
      <path d="M0 0 C1.13631866 -0.00700928 2.27263733 -0.01401855 3.44338989 -0.02124023 C7.17214935 -0.03873979 10.90014006 -0.02722525 14.62890625 -0.01367188 C17.23624822 -0.01636908 19.8435892 -0.02025339 22.45092773 -0.02526855 C27.90454319 -0.03252091 33.3579411 -0.02302117 38.81152344 -0.00390625 C45.0930662 0.01785813 51.37408943 0.01080502 57.65561962 -0.01121187 C63.72866314 -0.03163612 69.80147856 -0.02936635 75.87454224 -0.01768494 C78.44524204 -0.01502564 81.0159552 -0.01825919 83.5866394 -0.02760315 C87.1857158 -0.03781569 90.78380891 -0.02220012 94.3828125 0 C95.43453644 -0.00809692 96.48626038 -0.01619385 97.56985474 -0.02453613 C108.62987989 0.10119828 117.16775762 3.97117254 125.19140625 11.51757812 C132.24794 19.02037488 136.40469408 26.72855911 136.44140625 37.14257812 C136.37819504 38.93506752 136.30213917 40.72739598 136.19140625 42.51757812 C136.79726563 42.47117188 137.403125 42.42476562 138.02734375 42.37695312 C139.28417969 42.3228125 139.28417969 42.3228125 140.56640625 42.26757812 C141.37851562 42.22117188 142.190625 42.17476562 143.02734375 42.12695312 C151.74175193 43.20280599 158.89518811 47.93065587 165.87890625 52.95507812 C167.17646851 53.87824829 167.17646851 53.87824829 168.50024414 54.82006836 C185.20794451 66.87842805 200.69905588 81.0878698 213.19140625 97.51757812 C213.9179541 98.47011475 213.9179541 98.47011475 214.65917969 99.44189453 C223.12954738 110.62538615 230.48842301 122.19945955 237.19140625 134.51757812 C237.73788818 135.50177734 237.73788818 135.50177734 238.29541016 136.50585938 C254.30680439 165.53825136 264.39339558 201.34777171 264.32421875 234.57226562 C264.32565384 236.10731247 264.32565384 236.10731247 264.32711792 237.67337036 C264.32779429 239.81968244 264.32597819 241.96599662 264.32177734 244.11230469 C264.31638805 247.40369197 264.32175774 250.69494485 264.328125 253.98632812 C264.32746477 256.07747426 264.32618475 258.16862031 264.32421875 260.25976562 C264.32624298 261.24411591 264.32826721 262.22846619 264.33035278 263.24264526 C264.32720062 264.15434479 264.32404846 265.06604431 264.32080078 266.00537109 C264.31960739 267.2070845 264.31960739 267.2070845 264.31838989 268.43307495 C264.19140625 270.51757812 264.19140625 270.51757812 263.19140625 273.51757812 C263.94164062 273.84628906 264.691875 274.175 265.46484375 274.51367188 C278.73981424 280.68302214 288.82594927 289.86837286 294.3515625 303.59570312 C296.44857469 309.58444054 296.84681233 315.084611 296.75390625 321.39257812 C296.74367432 322.44324463 296.73344238 323.49391113 296.72290039 324.57641602 C296.317396 339.42614768 290.58835117 349.6247705 279.87890625 359.76757812 C271.84891069 366.6465163 262.47319578 370.76145923 251.8676672 370.77764034 C251.14876844 370.78144601 250.42986969 370.78525168 249.68918613 370.78917268 C247.27961012 370.79948124 244.87027002 370.79558367 242.4606781 370.79173279 C240.71584708 370.79618561 238.97101809 370.8014998 237.22619206 370.80760151 C232.43056326 370.82178698 227.63499926 370.8234447 222.83935249 370.8230046 C217.6673682 370.82465235 212.49541063 370.83771818 207.32344055 370.84919739 C196.0133667 370.87205085 184.70331161 370.87996853 173.3932178 370.88502383 C166.32986659 370.88828077 159.26652082 370.89480328 152.20317268 370.90202522 C132.64344044 370.92159243 113.08371326 370.93799566 93.5239706 370.94090176 C92.27218447 370.94108933 91.02039833 370.9412769 89.73067928 370.94147015 C87.19303421 370.9418364 84.65538915 370.9422001 82.11774408 370.94256126 C80.22822663 370.94283548 80.22822663 370.94283548 78.30053711 370.94311523 C76.40871068 370.94338606 76.40871068 370.94338606 74.47866554 370.94366235 C54.04709187 370.94720321 33.61562366 370.97421959 13.18408655 371.01161312 C-7.79711206 371.04970501 -28.77825837 371.0690593 -49.75949234 371.06936127 C-61.53785537 371.06994568 -73.31609265 371.07842693 -85.09442329 371.10735893 C-95.12504761 371.13188465 -105.15550031 371.13914765 -115.18614651 371.12411141 C-120.30172243 371.1169472 -125.41695956 371.11777908 -130.53249741 371.140522 C-135.2214945 371.16116835 -139.90989356 371.15845887 -144.59888458 371.13764716 C-146.289374 371.13416305 -147.97990707 371.13901625 -149.67033929 371.1533516 C-165.49646506 371.27884261 -177.46210821 367.62658491 -188.9609375 356.41992188 C-193.78849572 351.32266766 -197.14645631 346.00782793 -199.80859375 339.51757812 C-200.35580078 338.24140625 -200.35580078 338.24140625 -200.9140625 336.93945312 C-203.96297547 325.27636247 -203.53873827 310.09031332 -198.3515625 299.13476562 C-191.13302612 286.89066594 -181.71824423 279.28432555 -168.80859375 273.51757812 C-169.30359375 272.03257812 -169.30359375 272.03257812 -169.80859375 270.51757812 C-169.9093869 269.07951269 -169.95685926 267.63733673 -169.96972656 266.19580078 C-169.97933411 265.32283356 -169.98894165 264.44986633 -169.99884033 263.55044556 C-170.00309021 262.60373993 -170.00734009 261.6570343 -170.01171875 260.68164062 C-170.01741882 259.6924765 -170.0231189 258.70331238 -170.0289917 257.68417358 C-170.03845356 255.57754587 -170.04498037 253.47090327 -170.04882812 251.36425781 C-170.05836767 248.21716139 -170.08930649 245.07076645 -170.12109375 241.92382812 C-170.46016768 177.45747664 -141.63019041 119.3388604 -97.30859375 73.83007812 C-86.21538024 62.93216767 -65.75934141 43.06682016 -49.328125 42.41992188 C-48.43480469 42.43152344 -47.54148438 42.443125 -46.62109375 42.45507812 C-45.72003906 42.46410156 -44.81898438 42.473125 -43.890625 42.48242188 C-43.20355469 42.49402344 -42.51648438 42.505625 -41.80859375 42.51757812 C-41.855 41.76734375 -41.90140625 41.01710937 -41.94921875 40.24414062 C-42.32905611 29.47303824 -39.67422661 21.91534025 -32.78515625 13.61914062 C-23.44850666 3.69203098 -13.48703607 -0.10383286 0 0 Z M-12.80859375 31.58007812 C-18.93815055 40.58286467 -15.63689286 54.04576572 -14.41577148 64.37255859 C-14.18874638 66.37700922 -13.9617223 68.38145996 -13.73473656 70.38591504 C-13.49279011 72.50920185 -13.24700848 74.63201243 -13.00008965 76.75472641 C-12.48314454 81.20129635 -11.9787264 85.64923577 -11.47608948 90.09744263 C-10.22703145 101.13413023 -8.96128629 112.16889637 -7.69335938 123.20343018 C-6.70505889 131.80641822 -5.72117086 140.40986526 -4.74999958 149.01480591 C-4.25109624 153.43084591 -3.74338157 157.84575309 -3.22879207 162.25999141 C-2.83573972 165.65214371 -2.4548795 169.04564729 -2.07202148 172.43896484 C-1.85623695 174.25635414 -1.85623695 174.25635414 -1.63609314 176.11045837 C-0.08272906 190.12976967 -0.08272906 190.12976967 -3.49609375 196.83007812 C-6.67488388 200.52434774 -9.29059016 202.18045698 -14.171875 202.83398438 C-20.2469655 203.11976253 -23.98517273 202.25819034 -28.80859375 198.51757812 C-32.13062813 193.31770783 -32.65577495 188.11284409 -33.33178711 182.1184082 C-33.45451209 181.10668202 -33.57723707 180.09495584 -33.70368099 179.0525713 C-34.08488656 175.87559814 -34.44886936 172.69704945 -34.80859375 169.51757812 C-34.92067799 168.5439822 -35.03276222 167.57038628 -35.14824295 166.56728745 C-35.82088624 160.71304484 -36.46726582 154.8562013 -37.10180855 148.9977169 C-37.44809074 145.80670243 -37.79852778 142.61616781 -38.15013123 139.42573547 C-39.14731963 130.3762209 -40.14189921 121.32644247 -41.12280273 112.27514648 C-41.72825149 106.69111614 -42.34213019 101.10806896 -42.96165276 95.52558327 C-43.1948793 93.40915369 -43.42490559 91.29236891 -43.65167427 89.17523766 C-43.96625347 86.24112489 -44.29152616 83.30838656 -44.61938477 80.37573242 C-44.7103392 79.50268463 -44.80129364 78.62963684 -44.89500427 77.73013306 C-45.26719487 73.15522915 -45.26719487 73.15522915 -47.80859375 69.51757812 C-59.73690211 77.15752432 -70.45281224 85.85218205 -80.80859375 95.51757812 C-81.62070313 96.27554688 -82.4328125 97.03351563 -83.26953125 97.81445312 C-89.66023044 103.98339304 -95.14449012 110.65373879 -100.46923828 117.73828125 C-101.33068602 118.88269086 -102.19837481 120.02244313 -103.07373047 121.15625 C-128.86077825 154.58428337 -142.42544258 197.17468825 -144.43359375 239.08007812 C-144.48515625 239.96759766 -144.53671875 240.85511719 -144.58984375 241.76953125 C-144.98094402 249.87335896 -144.35922444 257.25811771 -143.80859375 265.51757812 C-17.74859375 265.51757812 108.31140625 265.51757812 238.19140625 265.51757812 C238.53779106 234.01938261 238.53779106 234.01938261 234.19140625 203.51757812 C234.00416992 202.67146973 233.81693359 201.82536133 233.62402344 200.95361328 C224.58151686 161.30870319 205.4003296 126.68544504 177.19140625 97.51757812 C176.44632812 96.7209375 175.70125 95.92429687 174.93359375 95.10351562 C165.30351143 85.047009 153.8830066 76.99078055 142.19140625 69.51757812 C139.78990357 71.9190808 139.831309 72.79683276 139.47755432 76.10133362 C139.31585754 77.55001076 139.31585754 77.55001076 139.15089417 79.0279541 C138.98641029 80.62298767 138.98641029 80.62298767 138.81860352 82.25024414 C138.69633392 83.36749374 138.57406433 84.48474335 138.4480896 85.635849 C138.18289918 88.06904117 137.92174525 90.5026758 137.66409492 92.93667793 C137.25561451 96.79223073 136.8361061 100.64643819 136.41355896 104.50047302 C135.36550642 114.07995869 134.33538541 123.66135287 133.30712891 133.24298096 C132.43939482 141.32582483 131.56339604 149.40767578 130.67652702 157.48844719 C130.26377564 161.27921799 129.86275815 165.07111443 129.46205139 168.86317444 C129.20650233 171.20189875 128.95038287 173.54056084 128.69360352 175.87915039 C128.58597183 176.92833649 128.47834015 177.97752258 128.3674469 179.0585022 C127.39291203 187.74090343 126.35223157 195.78891787 119.19140625 201.51757812 C113.76291445 203.32707539 108.22828015 203.19653609 102.81640625 201.39257812 C98.52050425 198.32407669 96.55347895 195.31072389 95.50697327 190.12796021 C94.87277082 184.1850244 95.75368758 178.34418874 96.45483398 172.43896484 C96.60099152 171.1435527 96.74714905 169.84814056 96.8977356 168.51347351 C97.21430474 165.70773606 97.5366751 162.90274946 97.86361504 160.09820175 C98.38189077 155.65058839 98.88602411 151.20143959 99.38819885 146.75198364 C100.36662262 138.09948954 101.35857146 129.44857412 102.35258484 120.79785919 C103.6149823 109.80913158 104.87327788 98.81996119 106.11524159 87.82890332 C106.61397122 83.41967074 107.12039902 79.01141557 107.63310421 74.60378718 C107.87513393 72.51281856 108.11352262 70.42147154 108.35037231 68.32991028 C108.63707579 65.7985472 108.92922045 63.2677907 109.2283783 60.73786926 C111.25120369 46.53992192 111.25120369 46.53992192 108.56640625 32.89257812 C101.51067515 25.83684703 95.39099097 25.36863299 85.95410156 25.35644531 C85.03148529 25.35148041 84.10886902 25.3465155 83.15829468 25.34140015 C80.11203255 25.32681928 77.06582251 25.31998307 74.01953125 25.31445312 C71.89589436 25.30869804 69.77225748 25.30294035 67.64862061 25.29718018 C63.1967342 25.28668158 58.74486579 25.28083154 54.29296875 25.27734375 C48.60256252 25.27186342 42.91239609 25.24784026 37.22206497 25.2193718 C32.83473019 25.20070101 28.44745263 25.19553743 24.06008148 25.19400787 C21.96302734 25.19099053 19.86597399 25.18299318 17.76895905 25.16982269 C14.82788617 25.15267043 11.88739236 25.15466493 8.94628906 25.16113281 C8.08883087 25.15191803 7.23137268 25.14270325 6.34793091 25.13320923 C-0.65725234 25.18120123 -8.03670012 25.97096775 -12.80859375 31.58007812 Z M-171.05859375 306.95507812 C-175.71623778 312.35143451 -176.26628934 317.58121136 -176.11328125 324.51757812 C-175.44649745 331.08283396 -172.65201438 334.9912391 -167.87109375 339.39257812 C-161.11653901 344.88611618 -154.41112333 345.64121921 -145.84849834 345.64621258 C-145.16892217 345.64796562 -144.489346 345.64971865 -143.78917661 345.65152481 C-141.51345786 345.65617437 -139.2378033 345.65372201 -136.96208191 345.65129089 C-135.31339714 345.65315939 -133.66471281 345.65546025 -132.01602918 345.65815622 C-127.48632622 345.66427582 -122.95664171 345.66414043 -118.42693555 345.66296184 C-113.54133976 345.66275788 -108.6557499 345.66827619 -103.77015686 345.67301941 C-94.19871909 345.68132736 -84.62728749 345.68407431 -75.05584642 345.68463179 C-67.27622147 345.68510687 -59.49659856 345.68716123 -51.71697426 345.69033241 C-29.66300172 345.69913931 -7.60903406 345.70375172 14.44494025 345.70300194 C16.2285022 345.702942 16.2285022 345.702942 18.0480957 345.70288086 C19.23858781 345.70283998 20.42907991 345.7027991 21.65564754 345.70275698 C40.95177586 345.70235048 60.24788466 345.71192095 79.54400734 345.72601923 C99.35448953 345.74037917 119.16496221 345.74728962 138.97544974 345.74642545 C150.09859426 345.74609335 161.22171833 345.74884603 172.34485817 345.7596035 C181.81487771 345.76870346 191.28486751 345.77088789 200.75488953 345.76409408 C205.58632773 345.76082636 210.41770889 345.76078131 215.24914169 345.76944733 C219.67376939 345.77730204 224.09829469 345.77589139 228.52291986 345.76716319 C230.1218138 345.76562595 231.72071534 345.76745284 233.31960032 345.77302327 C248.71520875 346.10548644 248.71520875 346.10548644 262.19140625 339.51757812 C267.5514921 334.11893051 270.46402539 329.5167378 270.56640625 321.70507812 C270.4161585 314.89384685 269.02274365 310.50259981 264.3203125 305.54492188 C256.94884927 298.75871659 250.14408387 297.39472439 240.23131084 297.38894367 C239.55173467 297.38719063 238.8721585 297.3854376 238.17198911 297.38363144 C235.89627036 297.37898188 233.6206158 297.38143424 231.34489441 297.38386536 C229.69620964 297.38199686 228.04752531 297.379696 226.39884168 297.37700003 C221.86913872 297.37088043 217.33945421 297.37101582 212.80974805 297.37219441 C207.92415226 297.37239837 203.0385624 297.36688006 198.15296936 297.36213684 C188.58153159 297.35382889 179.01009999 297.35108194 169.43865892 297.35052446 C161.65903397 297.35004938 153.87941106 297.34799502 146.09978676 297.34482384 C124.04581422 297.33601694 101.99184656 297.33140453 79.93787225 297.33215431 C78.1543103 297.33221425 78.1543103 297.33221425 76.3347168 297.33227539 C75.14422469 297.33231627 73.95373259 297.33235715 72.72716496 297.33239927 C53.43103664 297.33280577 34.13492784 297.3232353 14.83880516 297.30913702 C-4.97167703 297.29477708 -24.78214971 297.28786663 -44.59263724 297.2887308 C-55.71578176 297.2890629 -66.83890583 297.28631022 -77.96204567 297.27555275 C-87.43206521 297.26645279 -96.90205501 297.26426836 -106.37207703 297.27106217 C-111.20351523 297.27432989 -116.03489639 297.27437494 -120.86632919 297.26570892 C-125.29095689 297.25785421 -129.71548219 297.25926486 -134.14010736 297.26799306 C-135.7390013 297.2695303 -137.33790284 297.26770341 -138.93678782 297.26213298 C-156.77702114 297.14328429 -156.77702114 297.14328429 -171.05859375 306.95507812 Z " transform="translate(208.80859375,70.482421875)"/>
    </g>
  </svg>
);

const DOCS_SIN_CADUCIDAD = [5, 10];

// ─── Doc status icon (misma lógica que proveedor.jsx) ────────────────────────
const DocStatusIcon = ({ doc, noProcede }) => {
  if (noProcede) return <MinusCircle className="w-5 h-5 text-gray-300" />;
  if (doc?.pendiente) return <MinusCircle className="w-5 h-5 text-yellow-500" />;
  const noCaduca = DOCS_SIN_CADUCIDAD.includes(doc?.tipoDoc);
  if (doc?.validado && (noCaduca || !doc?.fechaCaducidad || new Date(doc.fechaCaducidad) > new Date())) {
    return <CheckCircle className="w-5 h-5 text-green-500" />;
  }
  return <XCircle className="w-5 h-5 text-red-500" />;
};

const getDocSubtext = (doc, noProcede, t) => {
  if (noProcede) return <span className="text-gray-400">{t('suppliers:detail.notApplicable', { defaultValue: 'No procede' })}</span>;
  const noCaduca = DOCS_SIN_CADUCIDAD.includes(doc?.tipoDoc);
  if (noCaduca || !doc?.fechaCaducidad) return "-";
  const expired = new Date(doc.fechaCaducidad) <= new Date();
  const dateStr = new Date(doc.fechaCaducidad).toLocaleDateString();
  if (expired) return <span className="text-red-500">{`Caducado el ${dateStr}`}</span>;
  return <span className="text-gray-500">{`Vigente hasta ${dateStr}`}</span>;
};

// ─── Doc row ─────────────────────────────────────────────────────────────────
const DocRow = ({ doc, label, noProcede, t, isLast }) => {
  const canView = !noProcede && (doc?.pendiente || doc?.validado);
  return (
    <div className="flex items-center gap-3 px-4 sm:px-8 py-2.5 border-b border-gray-50 last:border-0 hover:bg-gray-50/50" style={isLast ? { borderBottom: 'none' } : {}}>
      <DocStatusIcon doc={doc} noProcede={noProcede} />
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-gray-700">{label}</p>
        <p className="text-[11px] mt-0.5">{getDocSubtext(doc, noProcede, t)}</p>
      </div>
      {!noProcede && (
        <button
          onClick={() => canView && abrirArchivo(doc?.ruta)}
          className={`flex items-center gap-1 text-[11px] transition-colors whitespace-nowrap ${
            canView ? "text-gray-400 hover:text-gray-600 cursor-pointer" : "text-gray-300 cursor-default"
          }`}
        >
          <FileText className="w-3.5 h-3.5" />
          Ver documento
        </button>
      )}
    </div>
  );
};

// ─── Proveedor row ────────────────────────────────────────────────────────────
const ProveedorRow = ({ proveedor, expanded, onToggle, t, userId, isLast }) => {
  const pct = Math.round(proveedor.porcentajeDocs || 0);
  const circleColor = pct >= 60 ? "#10b981" : pct > 30 ? "#f97316" : pct === 0 ? "#7c7c7c": "#ff5656";

  const getDoc = (tipoDoc) => proveedor?.docssupps?.find(d => d.tipoDoc === tipoDoc);
  const getDeclRes = () => proveedor?.declres?.find(d => d.aaff_id === userId);

  const documents = [
    { id: 2, label: "RC", noProcede: false },
    { id: 3, label: t('suppliers:documentTypes.preventiveModality', { defaultValue: 'Modalidad preventiva' }), noProcede: proveedor?.noProcede },
    { id: 5, label: t('suppliers:documentTypes.riskAssessment', { defaultValue: 'Evaluación de riesgos' }), noProcede: false },
    { id: 4, label: t('suppliers:documentTypes.preventivePlanning', { defaultValue: 'Planificación preventiva' }), noProcede: proveedor?.noProcede },
    { id: 10, label: t('suppliers:documentTypes.workersList', { defaultValue: 'Listado de trabajadores' }), noProcede: proveedor?.noProcede },
    { id: 9, label: "RLC", noProcede: proveedor?.noProcede || getDoc(9)?.noProcede === true || getDoc(9)?.noProcede === 1 },
    { id: 8, label: "RNT", noProcede: proveedor?.noProcede || getDoc(8)?.noProcede === true || getDoc(8)?.noProcede === 1 },
    { id: 6, label: t('suppliers:documentTypes.socialSecurityCertFull', { defaultValue: 'Certificado corriente de pago Seguridad Social' }), noProcede: false },
  ];

  return (
    <div style={isLast ? {} : {borderBottom: "1px solid rgb(209 213 219)"}}>
      <div
        className="flex items-center gap-3 px-3 sm:px-5 py-3 cursor-pointer hover:bg-gray-50 transition-colors select-none"
        onClick={onToggle}
      >
        <span className="flex-1 text-sm text-gray-700">{proveedor.razonSocial}</span>
        <span className="flex items-center gap-1.5 text-xs text-gray-900 w-14 justify-start">
          <CircleProgress pct={pct} color={circleColor} />
          {pct}%
        </span>
        <svg
          className={`w-4 h-4 text-gray-300 transition-transform ${expanded ? "rotate-180" : ""}`}
          viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {expanded && (
        <div className="border-t border-gray-100">
          {documents.map((d) => (
            <DocRow key={d.id} doc={getDoc(d.id)} label={d.label} noProcede={d.noProcede} t={t} />
          ))}
          <DocRow
            doc={getDeclRes()}
            label={t('suppliers:documentTypes.responsibleDeclaration', { defaultValue: 'Declaración responsable' })}
            noProcede={false}
            t={t}
          />
          {/* Fila Trabajadores */}
          <div className="flex items-center gap-3 px-4 sm:px-8 py-2.5 hover:bg-gray-50/50" style={{ borderBottom: 'none' }}>
            {proveedor.docTrabajadores === null ? (
              <MinusCircle className="w-5 h-5 text-gray-300" />
            ) : proveedor.docTrabajadores ? (
              <CheckCircle className="w-5 h-5 text-green-500" />
            ) : (
              <XCircle className="w-5 h-5 text-red-500" />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-gray-700">{t('common:workers.title', { defaultValue: 'Trabajadores' })}</p>
              <p className="text-[11px] mt-0.5 text-gray-400">-</p>
            </div>
            {proveedor.docTrabajadores !== null && (
              <Link
                to={`/proveedores/${proveedor.nif}#trabajadores`}
                className="flex items-center gap-1 text-[11px] text-gray-400 hover:text-gray-600 transition-colors no-underline"
              >
                <Users className="w-3.5 h-3.5" />
                {t('common:workers.viewWorkers', { defaultValue: 'Ver trabajadores' })}
              </Link>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Documentos CAE ──────────────────────────────────────────────────────────
const enlaceToDocType = {
  "identificacion-riesgos": "evaluacionRiesgos",
  "hoja-visita": "hojaVisita",
  "medidas-emergencia": "planEmergencia",
};

const renderDocStatus = (nombre, icono, enlace, row) => {
  const documento = row.detailsCae?.find((obj) => obj.sale_id === row.id);

  if (!documento || (!documento.pendiente && !documento.visitada)) {
    return (
      <div key={enlace} className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2.5 flex-1 min-w-0 bg-white">
        <span className="text-gray-400 flex-shrink-0">{icono}</span>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-gray-500 truncate">{nombre}</p>
          <p className="text-[11px] text-red-400">Sin documento</p>
        </div>
      </div>
    );
  }

  const { pendiente, visitada, visitSheet_id } = documento;

  let url;
  if (pendiente || visitada) {
    url = row.workcenters === null
      ? `/doc-render/${row.properties?.nif}/${row.id}/${enlace}`
      : `/doc-render/${row.properties?.nif}/${row.workcenters.id}/${row.id}/${enlace}`;
  }
  if (!visitSheet_id) {
    const ruta = row.docsProps?.find((doc) => doc.type === enlaceToDocType[enlace])?.ruta;
    if (ruta) url = process.env.REACT_APP_STORAGE_URL + ruta;
  }

  const bgColor = visitada ? "bg-green-100 text-green-600" : "bg-red-100 text-red-600";

  return (
    <div key={enlace} className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2.5 flex-1 min-w-0 bg-white">
      <span className={`flex items-center justify-center w-7 h-7 rounded-full ${bgColor}`}>{icono}</span>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-gray-700 truncate">{nombre}</p>
      </div>
      {url && (
        <Enlace to={url} target="_blank">
          <span className="text-[11px] text-gray-400 hover:text-gray-600 font-medium cursor-pointer whitespace-nowrap">Ver</span>
        </Enlace>
      )}
    </div>
  );
};

// ─── Collapsible section ─────────────────────────────────────────────────────
const CollapsibleSection = ({ label, children, defaultOpen = false, onToggle }) => {
  const [open, setOpen] = useState(defaultOpen);
  const toggle = () => {
    const next = !open;
    setOpen(next);
    onToggle?.(next);
  };
  return (
    <div>
      <button
        onClick={toggle}
        className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 mb-2 hover:text-gray-700 transition-colors"
      >
        {label}
        <svg
          className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`}
          viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && children}
    </div>
  );
};

const RESP_RULES = {
  3:"si",2:"si",5:"si",6:"si",7:"si",8:"si",9:"no",11:"no",12:"no",14:"no",15:"no",16:"no",17:"no",
  18:"no",19:"no",20:"no",22:"no",23:"no",24:"no",27:"no",28:"no",29:"no",30:"no",32:"no",33:"no",
  34:"no",35:"no",36:"no",37:"no",38:"no",39:"no",40:"no",41:"no",43:"si",47:"si",48:"no",49:"no",
  50:"no",51:"no",53:"no",54:"no",56:"no",57:"no",58:"no",60:"no",61:"no",
};

const RiesgosSection = ({ sale, t }) => {
  const [riesgos, setRiesgos] = useState(null);
  const [loading, setLoading] = useState(false);
  const fetched = useRef(false);

  const visitSheetId = sale?.detailsCae?.[0]?.visitSheet_id;

  const handleToggle = async (isOpen) => {
    if (isOpen && !fetched.current && visitSheetId) {
      fetched.current = true;
      setLoading(true);
      try {
        const response = await getVisitSheet(visitSheetId);
        const questions = response.data?.questions || [];
        const activeKeys = new Set();
        if (Array.isArray(questions)) {
          questions.forEach((q) => {
            if (RESP_RULES[q.statements_id] === q.resp) {
              activeKeys.add(q.statements_id);
            }
          });
        }
        setRiesgos(riesgosData.filter((r) => activeKeys.has(r.key)));
      } catch (err) {
        console.error("Error fetching visitSheet:", err);
        setRiesgos([]);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <CollapsibleSection label={t("details.riskAssessment")} onToggle={handleToggle}>
      {loading && <p className="text-xs text-gray-400 py-2">{t("details.loading")}...</p>}
      {riesgos && riesgos.length === 0 && (
        <p className="text-xs text-gray-400 py-2">Sin riesgos identificados</p>
      )}
      {riesgos && riesgos.length > 0 && (
        <div className="space-y-3">
          {riesgos.map((r, i) => (
            <div key={i}>
              <p className="text-sm font-semibold text-gray-800">{r.riesgo.join(" - ")}</p>
              {r.medidas.map((m, j) => (
                <p key={j} className="text-xs text-gray-700 mt-0.5 leading-relaxed">{m}</p>
              ))}
            </div>
          ))}
        </div>
      )}
    </CollapsibleSection>
  );
};

const DocsRow = ({ sale, t }) => {
    
  const RIESGOS_DOCS = [
    { nombre: t("documents.riskAssessment"), icono: <MdHealthAndSafety className="w-4 h-4" />, enlace: "identificacion-riesgos" },
    { nombre: t("documents.emergencyPlan"), icono: <IoIosWarning className="w-4 h-4" />, enlace: "medidas-emergencia" },
  ];

  const VISITA_DOC = { nombre: t("documents.visitSheet"), icono: <HiClipboardDocumentList className="w-4 h-4" />, enlace: "hoja-visita" };

  return (
    <div className="flex flex-col gap-2">
        {renderDocStatus(VISITA_DOC.nombre, VISITA_DOC.icono, VISITA_DOC.enlace, sale)}
        {RIESGOS_DOCS.map((doc) => renderDocStatus(doc.nombre, doc.icono, doc.enlace, sale))}
        </div>
  )
};

// ─── Helpers ─────────────────────────────────────────────────────────────────
const ESTADO_BADGE_COLOR = { 1: 'green', 2: 'yellow', 3: 'yellow', 4: 'red', 5: 'red' };
// const getEstadoId = (sale) => sale?.estadosVentas?.find(i => i.id === sale?.id)?.estado_id ?? sale?.estado_id;
const getSaleYear = (sale) => sale?.saleDate ? new Date(sale.saleDate).getFullYear() : '';
const getVisitDate = (sale) => {
  const d = sale?.detailsCae?.[0]?.visitSheet?.visitDate;
  if (!d) return null;
  //   return new Date(d).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
  return formatoFechasEs(d);
};
const getEstadoBadgeColor = (estadoCae) => ESTADO_BADGE_COLOR[estadoCae ?? 1] || 'gray';
const isVisitada = (sale) => !!sale?.detailsCae?.[0]?.visitada;


// ─── Sale card (para ventas anteriores) ──────────────────────────────────────
const SaleCard = ({ sale, estadoCae, t }) => (
  <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
    <div className="flex flex-wrap items-center justify-between gap-2 px-3 sm:px-5 pt-4 pb-3 border-b border-gray-100">
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-gray-800">{sale?.serviceprops?.nombre || 'CAE'} {getSaleYear(sale)}</span>
        {/* <Badge label={estadoServicio(estadoCae, t)} color={getEstadoBadgeColor(estadoCae)} /> */}
      </div>
      {getVisitDate(sale) && <span className="text-xs text-gray-400">{t("excel.visitDate")}: {getVisitDate(sale)}</span>}
    </div>
    <div className="px-3 sm:px-5 py-4 space-y-4">
      <RiesgosSection sale={sale} t={t} />
      <div>
        <p className="text-xs font-semibold text-gray-500 mb-2">{t("tabs.documents")}</p>
        <DocsRow sale={sale} t={t} />
      </div>
    </div>
  </div>
);

// ─── LOPD file row ──────────────────────────────────────────────────────────
const LopdFileRow = ({ nombre, url, badge, isLast, collapsible, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={isLast ? {} : { borderBottom: '1px solid #f3f4f6' }}>
      <div
        className={`flex items-center gap-3 py-2.5 px-1 ${collapsible ? "cursor-pointer hover:bg-gray-50 select-none" : ""}`}
        onClick={collapsible ? () => setOpen(prev => !prev) : undefined}
      >
        <FileText className="w-5 h-5 text-gray-300 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm text-gray-700">{nombre}</p>
        </div>
        {badge && (
          <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">{badge}</span>
        )}
        {url && (
          <Enlace to={url} target="_blank" onClick={e => e.stopPropagation()}>
            <span className="text-sm text-gray-400 hover:text-gray-600 cursor-pointer">Ver</span>
          </Enlace>
        )}
        {!url && !collapsible && <span className="text-sm text-gray-300">Ver</span>}
        {collapsible && (
          <svg
            className={`w-4 h-4 text-gray-300 transition-transform ${open ? "rotate-180" : ""}`}
            viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        )}
      </div>
      {collapsible && open && children}
    </div>
  );
};

// ─── LOPD doc card (grid item) ──────────────────────────────────────────────
const LopdDocCard = ({ nombre, url, badge }) => {
  const inner = (
    <div className={`bg-white border border-gray-200 rounded-lg p-4 h-full transition-all ${
      url ? 'hover:border-gray-300 hover:shadow-sm cursor-pointer' : 'opacity-70 cursor-default'
    }`}>
      <div className="flex items-start justify-between mb-3">
        <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
          <FileText className="w-5 h-5 text-purple-500" strokeWidth={1.8} />
        </div>
        {badge && (
          <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">{badge}</span>
        )}
      </div>
      <p className="text-sm font-medium text-gray-800 leading-snug mb-1 break-words">{nombre}</p>
      <p className="text-[11px] text-gray-400">{url ? 'Click para abrir' : 'No disponible'}</p>
    </div>
  );
  if (!url) return inner;
  return (
    <Enlace to={url} target="_blank">
      {inner}
    </Enlace>
  );
};

// ─── LOPD sale card ─────────────────────────────────────────────────────────
const LopdSaleCard = ({ sale, t }) => {
  const estadoId = sale?.estadosVentas?.find(e => e.id === sale?.id)?.estado_id;
  const servpId = sale.serviceprops?.id || sale.servp_id;
  const detailLopd = sale.detailsLopd?.find(obj => obj.sale_id === sale.id);

  // Build document list
  const docs = [];
  if (servpId === 2 || servpId === 4) {
    const isAudit = servpId === 4;
    docs.push({ nombre: isAudit ? "Certificado-auditoria.pdf" : "Certificado-implantacion.pdf", enlace: isAudit ? "certificado-auditoria" : "certificado-implantacion", isCert: true });
    docs.push({ nombre: "Documento-implantacion.pdf", enlace: "documentacion-implantacion", hasContent: true });
    if (detailLopd?.surveillanceVideo) {
      docs.push({ nombre: "Video-vigilancia.pdf", enlace: "video-vigilancia" });
    }
    if (isAudit) {
      docs.push({ nombre: "Documentación-auditoria.pdf", enlace: "documentacion-auditoria", hasContent: true });
    }
  } else if (servpId === 5) {
    docs.push({ nombre: "LOPD.pdf", enlace: "lopd", isFile: true });
  }

  const getDocUrl = (doc) => {
    if (doc.isFile) {
      const ruta = sale.docsProps?.find(d => d.type === "LOPD")?.ruta;
      return ruta ? process.env.REACT_APP_STORAGE_URL + ruta : null;
    }
    if (detailLopd?.pendiente || detailLopd?.visitada) {
      return sale.workcenters === null
        ? `/doc-render/${sale.properties?.nif}/${sale.id}/${doc.enlace}`
        : `/doc-render/${sale.properties?.nif}/${sale.workcenters.id}/${sale.id}/${doc.enlace}`;
    }
    return null;
  };

  const docRenderUrl = (detailLopd?.pendiente || detailLopd?.visitada)
    ? (sale.workcenters === null
      ? `/doc-render/${sale.properties?.nif}/${sale.id}/documentacion-implantacion`
      : `/doc-render/${sale.properties?.nif}/${sale.workcenters.id}/${sale.id}/documentacion-implantacion`)
    : null;

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-3 sm:px-5 pt-4 pb-3" style={{ borderBottom: '1px solid #e5e7eb' }}>
        <span className="text-sm font-semibold text-gray-800">
          {sale?.serviceprops?.nombre || 'LOPD'} {getSaleYear(sale)}
        </span>
        {sale?.saleDate && (
          <span className="text-xs text-gray-400">Fecha renovación: {formatoFechasEs(sale.saleDate)}</span>
        )}
      </div>

      {/* Document files */}
      <div className="px-3 sm:px-5 pt-3 pb-1">
        {docs.map((doc, i) => (
          <LopdFileRow
            key={doc.enlace}
            nombre={doc.nombre}
            url={getDocUrl(doc)}
            badge={doc.isCert && detailLopd?.visitada ? "Favorable" : null}
            isLast={i === docs.length - 1}
          />
        ))}
      </div>
    </div>
  );
};

// ─── CAI sale card ──────────────────────────────────────────────────────────
const getCaiDetail = (sale) => {
  const list = sale?.detailsCai || [];
  return (
    list.find((d) => d?.visitada) ||
    list.find((d) => d?.visitSheetCai?.visitDate || d?.visitSheetCai_id) ||
    list.find((d) => d?.pendiente) ||
    list.find((d) => Number(d?.sale_id) === Number(sale?.id)) ||
    list[0] ||
    null
  );
};

const isVisitadaCai = (detailCai) =>
  !!(detailCai && (detailCai.visitada || detailCai.visitSheetCai?.visitDate || detailCai.visitSheetCai_id));

const isAccesibleCai = (detailCai) =>
  isVisitadaCai(detailCai) || !!detailCai?.pendiente;

const getCaiVisitDate = (detailCai) => {
  const d = detailCai?.visitSheetCai?.visitDate;
  return d ? formatoFechasEs(d) : null;
};

// ─── CAI doc card (grid item, mismo estilo que LopdDocCard) ──────────────────
const CaiDocCard = ({ nombre, icono, url, visitada, pendiente }) => {
  const iconBg = visitada ? "bg-green-50" : pendiente ? "bg-yellow-50" : "bg-red-50";
  const iconColor = visitada ? "text-green-500" : pendiente ? "text-yellow-500" : "text-red-500";
  const inner = (
    <div className={`bg-white border border-gray-200 rounded-lg p-4 h-full transition-all ${
      url ? 'hover:border-gray-300 hover:shadow-sm cursor-pointer' : 'opacity-70 cursor-default'
    }`}>
      <div className="flex items-start justify-between mb-3">
        <div className={`w-10 h-10 rounded-lg ${iconBg} flex items-center justify-center flex-shrink-0 ${iconColor}`}>
          {icono}
        </div>
      </div>
      <p className="text-sm font-medium text-gray-800 leading-snug mb-1 break-words">{nombre}</p>
      <p className="text-[11px] text-gray-400">{url ? 'Click para abrir' : 'No disponible'}</p>
    </div>
  );
  if (!url) return inner;
  return (
    <Enlace to={url} target="_blank">
      {inner}
    </Enlace>
  );
};

const CaiSaleCard = ({ sale, t }) => {
  const detailCai = getCaiDetail(sale);
  const visitada = isVisitadaCai(detailCai);
  const accesible = isAccesibleCai(detailCai);
  const visitDate = getCaiVisitDate(detailCai);

  const docs = [
    {
      nombre: t("documents.accessibilityCertificate", { defaultValue: "Certificado de accesibilidad" }),
      icono: <Award className="w-5 h-5" strokeWidth={1.8} />,
      enlace: "certificado-accesibilidad",
    },
    {
      nombre: t("documents.accessibilityReport", { defaultValue: "Informe de accesibilidad" }),
      icono: <HiClipboardDocumentList className="w-5 h-5" />,
      enlace: "informe-accesibilidad",
    },
  ];

  const getCaiDocUrl = () =>
    sale.workcenters === null
      ? `/doc-render/${sale.properties?.nif}/${sale.id}`
      : `/doc-render/${sale.properties?.nif}/${sale.workcenters.id}/${sale.id}`;

  const badgeLabel = visitada
    ? t("details.visited", { defaultValue: "Visitada" })
    : detailCai?.pendiente
      ? t("details.pendingProcessing", { defaultValue: "En proceso" })
      : t("details.notVisited", { defaultValue: "No visitada" });
  const badgeColor = visitada ? "green" : detailCai?.pendiente ? "yellow" : "red";

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-2 px-3 sm:px-5 pt-4 pb-3 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-800">
            {sale?.serviceprops?.nombre || 'CAI'} {getSaleYear(sale)}
          </span>
          <Badge label={badgeLabel} color={badgeColor} />
        </div>
        {visitDate && (
          <span className="text-xs text-gray-400">{t("excel.visitDate")}: {visitDate}</span>
        )}
      </div>
      {!accesible ? (
        <PendingVisit />
      ) : (
        <div className="p-4">
          <p className="text-xs font-semibold text-gray-500 mb-3">{t("tabs.documents")}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {docs.map((doc) => (
              <CaiDocCard
                key={doc.enlace}
                nombre={doc.nombre}
                icono={doc.icono}
                url={accesible ? `${getCaiDocUrl()}/${doc.enlace}` : null}
                visitada={visitada}
                pendiente={detailCai?.pendiente}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── KAE instalacion card ───────────────────────────────────────────────────
const ESTADO_KAE_STYLES = {
  1: "bg-green-100 text-green-700",
  4: "bg-red-100 text-red-600",
  5: "bg-red-200 text-red-700",
};

const KaeInstalacionCard = ({ instalacion, t }) => {
  const ano = instalacion.invoiceDate ? new Date(instalacion.invoiceDate).getFullYear() : "";
  const senales = (instalacion.productos || []).reduce((sum, p) => sum + (p.cantidad || 0), 0);
  const tecnicoNombre = instalacion.technician
    ? `${instalacion.technician.nombre} ${instalacion.technician.apellidos}`
    : "";
  const ubicacion = [
    instalacion.propertie?.direccion,
    instalacion.propertie?.codigoPostal,
    instalacion.propertie?.municipio,
    instalacion.propertie?.provincia,
  ]
    .filter(Boolean)
    .join(", ");
  const estadoNombre = instalacion.stateProps?.nombre;
  const estadoClass = ESTADO_KAE_STYLES[instalacion.estado_id] || "bg-gray-100 text-gray-600";

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-2 px-3 sm:px-5 pt-4 pb-3 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-800">KAE {ano}</span>
          {estadoNombre && (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-lg text-xs font-medium ${estadoClass}`}>
              {t(`common:stateNames.${estadoNombre}`, { defaultValue: estadoNombre })}
            </span>
          )}
          {instalacion.realizada ? (
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg text-xs font-medium bg-green-100 text-green-700">
              <CheckCircle className="w-3.5 h-3.5" />
              {t("common:status.completed", { defaultValue: "Realizada" })}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg text-xs font-medium bg-orange-100 text-orange-600">
              <XCircle className="w-3.5 h-3.5" />
              {t("common:status.pending", { defaultValue: "Pendiente" })}
            </span>
          )}
        </div>
        {instalacion.realizada && (
          <button
            onClick={() =>
              descargarCertificadoKae({
                tecnicoNombre,
                ubicacionFinca: ubicacion,
                lugar: instalacion.propertie?.municipio || instalacion.propertie?.provincia || "",
                fecha: instalacion.fecha_instalacion,
                nifPropiedad: instalacion.propertie?.nif,
              })
            }
            className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-lg bg-white text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
            style={{ border: "1px solid #e5e7eb" }}
          >
            <Download size={14} />
            {t("common:installations.detail.downloadCertificate", { defaultValue: "Descargar certificado" })}
          </button>
        )}
      </div>
      <div className="px-3 sm:px-5 py-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 mb-4">
          <div className="flex items-start gap-2">
            <Calendar size={16} className="text-[#3651d8] mt-0 shrink-0" strokeWidth={1.5} />
            <div className="min-w-0">
              <div className="text-[11px] text-gray-900 uppercase tracking-wide">
                {t("common:installations.columns.invoiceDate", { defaultValue: "Fecha factura" })}
              </div>
              <div className="text-sm text-gray-700">
                {instalacion.invoiceDate ? formatoFechasEs(instalacion.invoiceDate) : "—"}
              </div>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <ToolboxIcon size={16} className="text-[#3651d8] mt-0 shrink-0" strokeWidth={1.5} />
            <div className="min-w-0">
              <div className="text-[11px] text-gray-900 uppercase tracking-wide">
                {t("common:installations.columns.technician", { defaultValue: "Técnico" })}
              </div>
              <div className="text-sm text-gray-700">{tecnicoNombre || "—"}</div>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <Calendar size={16} className="text-[#3651d8] mt-0 shrink-0" strokeWidth={1.5} />
            <div className="min-w-0">
              <div className="text-[11px] text-gray-900 uppercase tracking-wide">
                {t("common:installations.columns.performedDate", { defaultValue: "Fecha realización" })}
              </div>
              <div className="text-sm text-gray-700">
                {instalacion.realizada && instalacion.fecha_instalacion
                  ? formatoFechasEs(instalacion.fecha_instalacion)
                  : "—"}
              </div>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <Package size={16} className="text-[#3651d8] mt-0 shrink-0" strokeWidth={1.5} />
            <div className="min-w-0">
              <div className="text-[11px] text-gray-900 uppercase tracking-wide">
                {t("common:installations.columns.signals", { defaultValue: "Señales" })}
              </div>
              <div className="text-sm font-semibold text-gray-900">{senales}</div>
            </div>
          </div>
        </div>

        {instalacion.productos && instalacion.productos.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-gray-500 mb-2">
              {t("common:installations.detail.products", { defaultValue: "Productos" })}
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
                    <th className="text-left text-xs font-medium text-gray-500 pb-2 pr-4">
                      {t("common:installations.columns.product", { defaultValue: "Producto" })}
                    </th>
                    <th className="text-left text-xs font-medium text-gray-500 pb-2 pr-4">
                      {t("common:installations.columns.warehouse", { defaultValue: "Almacén" })}
                    </th>
                    <th className="text-right text-xs font-medium text-gray-500 pb-2">
                      {t("common:installations.columns.quantity", { defaultValue: "Cantidad" })}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {instalacion.productos.map((item) => (
                    <tr key={item.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td className="py-2 pr-4">
                        <div className="font-medium text-gray-900">{item.producto?.nombre}</div>
                        <div className="text-xs text-gray-700">{item.producto?.sku}</div>
                      </td>
                      <td className="py-2 pr-4 text-gray-600">{item.almacen?.nombre || "—"}</td>
                      <td className="py-2 text-right font-semibold text-gray-900">{item.cantidad}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Componente principal ────────────────────────────────────────────────────
const Comunidad = ({ t, finca = {}, proveedores, prevNif = null, nextNif = null }) => {
  const user = useContext(UsuarioContext);
  const [activeTab, setActiveTab] = useState(finca?.gruposServicios?.[0] || 1);
  const [expandedProv, setExpandedProv] = useState({});
  const [data, setData] = useState({ meta: {}, rows: [] });
  const [kaeInstalaciones, setKaeInstalaciones] = useState([]);
  const [selectedCaeSaleId, setSelectedCaeSaleId] = useState(null);
  const [selectedLopdSaleId, setSelectedLopdSaleId] = useState(null);
  const refControladorAborto = useRef(null);

  useEffect(() => {
    if (!finca.id) return;

    if (refControladorAborto.current) refControladorAborto.current.abort();
    const controller = new AbortController();
    refControladorAborto.current = controller;

    getSales({
      filtros: { prop_id: finca.id },
      params: { page: 1, limit: 50, order: "DESC", orderBy: "saleDate" },
      signal: controller.signal,
    })
      .then((response) => setData({ meta: response.meta, rows: response.data }))
      .catch((error) => {
        if (error.name !== "CanceledError" && error.name !== "AbortError") {
          console.error("Error fetching sales:", error);
        }
      });

    getInstalaciones({
      filtros: { prop_id: finca.id },
      params: { page: 1, limit: 100, order: "DESC", orderBy: "invoiceDate" },
    })
      .then((response) => {
        if (response?.data) setKaeInstalaciones(response.data);
      })
      .catch((error) => {
        if (error?.name !== "CanceledError" && error?.name !== "AbortError") {
          console.error("Error fetching KAE installations:", error);
        }
      });

    return () => controller.abort();
  }, [finca.id]);

  useEffect(() => {
    const grupos = finca?.gruposServicios || [];
    const tabsVisibles = [
      grupos.includes(1) ? 1 : null,
      grupos.includes(2) ? 2 : null,
      grupos.includes(3) ? 3 : null,
      kaeInstalaciones.length > 0 ? 4 : null,
    ].filter(Boolean);
    if (tabsVisibles.length > 0 && !tabsVisibles.includes(activeTab)) {
      setActiveTab(tabsVisibles[0]);
    }
  }, [finca?.gruposServicios, kaeInstalaciones.length, activeTab]);

  const allRows = [...(data.rows || [])].sort((a, b) => b.id - a.id);
  const caeSales = allRows.filter(s => [1, 3, 6, 7].includes(s.servp_id));
  const lopdSales = allRows.filter(s => [2, 4, 5].includes(s.servp_id));
  const caiSales = allRows.filter(s => [8, 9].includes(s.servp_id));
  const ultimaVenta = caeSales[0] || null;
  const estadoCae = ultimaVenta?.estadosVentas?.find(e => e.id === ultimaVenta?.id)?.estado_id;
  const ventasAnteriores = caeSales.slice(1);
  const selectedCaeSale = caeSales.find(s => s.id === selectedCaeSaleId) || ultimaVenta;
  const selectedCaeEstado = selectedCaeSale?.estadosVentas?.find(e => e.id === selectedCaeSale?.id)?.estado_id;
  const selectedLopdSale = lopdSales.find(s => s.id === selectedLopdSaleId) || lopdSales[0] || null;
  const selectedLopdEstado = selectedLopdSale?.estadosVentas?.find(e => e.id === selectedLopdSale?.id)?.estado_id;
  const toggleProv = (i) => setExpandedProv((prev) => ({ ...prev, [i]: !prev[i] }));
  const puntuacion = finca?.porcentajeDocs === 100 ? "#10b981" : finca?.porcentajeDocs >= 50 ? "#f59e0b" : "#7c7c7c";
  const mediaProveedores = proveedores?.length > 0
    ? Math.round(proveedores.reduce((sum, p) => sum + (p.porcentajeDocs || 0), 0) / proveedores.length)
    : 0;
  const cumplimiento = Math.round(finca.porcentajeDocs || 0);

  return (
    <div className="min-h-screen bg-gray-50 font-sans text-gray-900 overflow-x-hidden">

      {/* ── Breadcrumb ── */}
      <div className="px-3 sm:px-6 flex items-center justify-between gap-2" style={{ borderBottom: '1px solid #e5e7eb' }}>
        <BreadCrumb rutas={[{ label: t('title'), path: '/comunidades' }, { label: finca.razonSocial }]} />
        <PrevNextNav prev={prevNif} next={nextNif} basePath="/comunidades" />
      </div>

      {/* ── Header ── */}
      <div className="mx-auto p-3 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-6 pl-2 pb-2" style={{ borderBottom: '1px solid #e5e7eb' }}>
          <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{finca.razonSocial}</h1>
            <span className="text-sm text-gray-900">{finca?.municipio}</span>
          </div>
          <span
            className="shrink-0 px-3 py-1.5 rounded-lg text-sm font-semibold"
            style={{
              backgroundColor: cumplimiento > 50 ? '#d1fae5' : cumplimiento > 0 ? '#fff7ed' : '#fee2e2',
              color: cumplimiento > 50 ? '#065f46' : cumplimiento > 0 ? '#c2410c' : '#991b1b',
            }}
          >
            {t('columns.compliance', { defaultValue: 'Cumplimiento' })}: {cumplimiento}/100
          </span>
        </div>
        {/* <div className="pb-3 flex items-center justify-end">
            <button class="p-2 border rounded-md flex items-center gap-1.5 text-xs text-gray-700 hover:text-gray-700 transition-colors">
                <PencilLine className="w-4 h-4" />    
                {t("common:actions.edit")}
            </button>
        </div> */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-x-6 lg:gap-y-0 pl-2">
            <div className="pt-2 px-4 order-1 bg-white border border-gray-200 rounded-xl overflow-hidden">
                <h2 className="text-base font-bold text-gray-900 mb-3">{t('details.communityData')}</h2>
                <dl className="text-sm mb-6">
                    <div className="flex justify-between py-2" style={{ borderBottom: '1px solid #e5e7eb' }}><dt className="text-gray-500 font-medium"><House className="text-[#3651d8] mr-2 mb-1 w-5 h-5" strokeWidth={1.5} />{t('details.communityName')}</dt><dd className="text-gray-900 font-medium">{finca.razonSocial}</dd></div>
                    <div className="flex justify-between py-2" style={{ borderBottom: '1px solid #e5e7eb' }}><dt className="text-gray-500 font-medium"><IdCard className="text-[#3651d8] mr-2 mb-1 w-5 h-5" strokeWidth={1.5} />NIF</dt><dd className="text-gray-900">{finca.nif}</dd></div>
                    <div className="flex justify-between py-2" style={{ borderBottom: '1px solid #e5e7eb' }}><dt className="text-gray-500 font-medium"><Locate className="text-[#3651d8] mr-2 mb-1 w-5 h-5" strokeWidth={1.5} />{t('common:form.address')}</dt><dd className="text-gray-900">{finca.direccion}</dd></div>
                    <div className="flex justify-between py-2" style={{ borderBottom: '1px solid #e5e7eb' }}><dt className="text-gray-500 font-medium"><MapPin className="text-[#3651d8] mr-2 mb-1 w-5 h-5" strokeWidth={1.5} />{t('common:form.municipality')}</dt><dd className="text-gray-900">{cambiarFormatoNombre(finca?.municipio)}</dd></div>
                    <div className="flex justify-between py-2" style={{ borderBottom: '1px solid #e5e7eb' }}><dt className="text-gray-500 font-medium"><Mailbox className="text-[#3651d8] mr-2 mb-1 w-5 h-5" strokeWidth={1.5} />{t('common:form.postalCode')}</dt><dd className="text-gray-900">{finca.codigoPostal}</dd></div>
                    <div className="flex justify-between py-2" style={{ borderBottom: '1px solid #e5e7eb' }}><dt className="text-gray-500 font-medium"><MapPinned className="text-[#3651d8] mr-2 mb-1 w-5 h-5" strokeWidth={1.5} />{t('common:form.province')}</dt><dd className="text-gray-900">{finca.provincia}</dd></div>
                    <div className="flex justify-between py-2"><dt className="text-gray-500 font-medium"><User className="text-[#3651d8] mr-2 mb-1 w-5 h-5" strokeWidth={1.5} />{t('common:form.president')}</dt><dd className="text-gray-900">{finca.nombrePresidente ? finca.nombrePresidente : '-'}</dd></div>
                </dl>
            </div>

            {/* Datos contacto — móvil: 2º, desktop: col der */}
            <div className="pt-2 px-4 order-2 bg-white border border-gray-200 rounded-xl overflow-hidden">
                <h2 className="text-base font-bold text-gray-900 mb-3">{t('details.contactData')}</h2>
                <dl className="text-sm">
                    <div className="flex justify-between py-2" style={{ borderBottom: '1px solid #e5e7eb' }}><dt className="text-gray-500 font-medium"><SquareUserRound className="text-[#3651d8] mr-2 mb-1 w-5 h-5" strokeWidth={1.5} />{t('detail.contactPerson', { defaultValue: 'Pers. de contacto' })}</dt><dd className="text-gray-900">{finca.nombreContacto}</dd></div>
                    <div className="flex justify-between py-2" style={{ borderBottom: '1px solid #e5e7eb' }}><dt className="text-gray-500 font-medium"><AtSign className="text-[#3651d8] mr-2 mb-1 w-5 h-5" strokeWidth={1.5} />{t('common:form.email')}</dt><dd className="text-gray-900 truncate ml-4">{finca.emailContacto}</dd></div>
                    <div className="flex justify-between py-2"><dt className="text-gray-500 font-medium"><Phone className="text-[#3651d8] mr-2 mb-1 w-5 h-5" strokeWidth={1.5} />{t('common:form.phone')}</dt><dd className="text-gray-900">{finca.telefonoContacto}</dd></div>
                </dl>
            </div>
        </div>
      </div>
      {/* ── Tabs ── */}
      <div className="bg-white border-b border-gray-200 px-3 sm:px-6">
        <div className="flex gap-4 sm:gap-6">
          {finca?.gruposServicios?.includes(1) && (
            <TabButton label="CAE" active={activeTab === 1} onClick={() => setActiveTab(1)} />
          )}
          {finca?.gruposServicios?.includes(2) && (
            <TabButton label="LOPD" active={activeTab === 2} onClick={() => setActiveTab(2)} />
          )}
          {finca?.gruposServicios?.includes(3) && (
            <TabButton label="CAI" active={activeTab === 3} onClick={() => setActiveTab(3)} />
          )}
          {kaeInstalaciones.length > 0 && (
            <TabButton label="KAE" active={activeTab === 4} onClick={() => setActiveTab(4)} />
          )}
        </div>
      </div>

      {/* ── Page content ── */}
      <div className="p-3 sm:p-6">

        {/* ═══ CAE TAB ═══ */}
        {activeTab === 1 && (
          <div className="space-y-4 max-w-8xl">

            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-md font-semibold text-gray-800">{t('details.comunityService', { service: "CAE" })}</span>

              {proveedores?.length > 0 && (
                <ExportToExcel
                  apiData={proveedores.map(p => ({
                    [t('suppliers:columns.name', { defaultValue: 'Proveedor' })]: p.razonSocial,
                    NIF: p.nif,
                    [t('suppliers:columns.compliance', { defaultValue: 'Cumplimiento' })]: `${Math.round(p.porcentajeDocs || 0)}%`,
                  }))}
                  fileName={`proveedores_${finca.nif || "comunidad"}`}
                  className="p-2 border rounded-md flex items-center gap-1.5 text-xs text-gray-700 hover:text-gray-700 transition-colors"
                >
                  <CloudDownload className="w-4 h-4"/>
                  {t('common:actions.downloadList')}
                </ExportToExcel>
              )}
            </div>

            {ultimaVenta ? (
              <>
                {/* Alertas (sobre la última venta) */}
                {estadoCae === 2 && isVisitada(ultimaVenta) &&
                    <div className="flex items-center gap-2 bg-orange-50 border border-orange-200 rounded-lg px-4 py-2.5">
                        <IoIosWarning className="w-4 h-4 text-orange-500 flex-shrink-0" />
                        <p className="text-xs text-orange-700"
                          dangerouslySetInnerHTML={{ __html: t('details.inspectionExpires', { date: getVisitDate(ultimaVenta) }) }}
                        />
                    </div>
                }
                {estadoCae === 4 && isVisitada(ultimaVenta) &&
                    <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-2.5">
                        <IoIosWarning className="w-4 h-4 text-red-500 flex-shrink-0" />
                        <p className="text-xs text-red-700"
                          dangerouslySetInnerHTML={{ __html: t('details.inspectionExpired', { date: formatoFechas(ultimaVenta.saleDate) }) }}
                        />
                    </div>
                }

                {/* ── 3 columnas: Servicios · Renovación (con pills) · Proveedores ── */}
                <div className="flex flex-col lg:flex-row lg:justify-between gap-4 items-start">

                  {/* Col 1: Servicios CAE de la comunidad */}
                  <div className="w-full lg:w-[24%] bg-white border border-gray-200 rounded-xl p-4">
                    <p className="text-xs font-semibold text-gray-900 uppercase tracking-wide mb-3">
                      {t('details.comunityService', { service: "CAE" })}
                    </p>
                    <div className="space-y-2">
                      <StatBox label={t('details.cumplimientoLevel')} value={ultimaVenta.serviceprops?.nombre || 'CAE'} />
                      <StatBox label={t('details.cumplimientoLevelSupps')} value={`${mediaProveedores}%`} />
                      <StatBox label={t('details.status')} value={estadoServicio(estadoCae, t)} />
                    </div>
                  </div>

                  {/* Col 2: Renovación seleccionada + pills de años */}
                  <div className="w-full lg:w-[36%] bg-white border border-gray-200 rounded-xl overflow-hidden">
                    <div className="px-4 pt-4 pb-2 border-b border-gray-100">
                      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-md font-semibold text-gray-800">
                            {selectedCaeSale?.serviceprops?.nombre || 'CAE'} {getSaleYear(selectedCaeSale)}
                          </span>
                          <Badge label={estadoServicio(selectedCaeEstado, t)} color={getEstadoBadgeColor(selectedCaeEstado)} />
                        </div>
                        {getVisitDate(selectedCaeSale) && (
                          <span className="text-xs text-gray-900">{t("excel.visitDate")}: {getVisitDate(selectedCaeSale)}</span>
                        )}
                      </div>
                      {caeSales.length > 1 && (
                        <div className="flex flex-wrap items-center gap-1.5">
                          {caeSales.map((sale) => {
                            const isActive = selectedCaeSale?.id === sale.id;
                            return (
                              <button
                                key={sale.id}
                                type="button"
                                onClick={() => setSelectedCaeSaleId(sale.id)}
                                className={`text-xs px-2.5 py-1 rounded-full transition-colors border-none cursor-pointer ${
                                  isActive
                                    ? 'bg-[#3651D8] text-white font-semibold'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                              >
                                {getSaleYear(sale)}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    {!isVisitada(selectedCaeSale) ? (
                      <PendingVisit />
                    ) : (
                      <div className="px-4 pt-2 pb-4 space-y-6">
                        <RiesgosSection sale={selectedCaeSale} t={t} />
                        <div>
                          <p className="text-xs font-semibold text-gray-500 mb-2">{t("tabs.documents")}</p>
                          <DocsRow sale={selectedCaeSale} t={t} />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Col 3: Cumplimiento de proveedores */}
                  <div className="w-full lg:w-[32%] bg-white border border-gray-200 rounded-xl overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-100">
                      <p className="text-md font-semibold text-gray-800">{t("details.suppsCumpliment")}</p>
                    </div>
                    {proveedores?.length > 0 ? (
                      proveedores.map((prov, i) => (
                        <ProveedorRow
                          key={prov.nif || i}
                          proveedor={prov}
                          expanded={!!expandedProv[i]}
                          onToggle={() => toggleProv(i)}
                          t={t}
                          userId={user?.id}
                          isLast={i === proveedores.length - 1}
                        />
                      ))
                    ) : (
                      <div className="px-5 py-6 text-center">
                        <p className="text-xs text-gray-400">{t("details.noSuppsAssoc")}</p>
                      </div>
                    )}
                  </div>

                </div>
              </>
            ) : (
              <div className="bg-white border border-gray-200 rounded-xl px-5 py-8 text-center">
                <p className="text-sm text-gray-400">{t('details.noComunityService', { service: "CAE" })}</p>
              </div>
            )}
          </div>
        )}

        {/* ═══ LOPD TAB ═══ */}
        {activeTab === 2 && (
          <div className="space-y-4 max-w-8xl">
            <span className="text-md font-semibold text-gray-800">{t('details.comunityService', { service: "LOPD" })}</span>
            {lopdSales.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 items-start">
              {lopdSales.map((sale) => {
                const estadoId = sale?.estadosVentas?.find(e => e.id === sale?.id)?.estado_id;
                const servpId = sale.serviceprops?.id || sale.servp_id;
                const detailLopd = sale.detailsLopd?.find(obj => obj.sale_id === sale.id);
                const docs = [];
                if (servpId === 2 || servpId === 4) {
                  const isAudit = servpId === 4;
                  docs.push({ nombre: isAudit ? "Certificado-auditoria.pdf" : "Certificado-implantacion.pdf", enlace: isAudit ? "certificado-auditoria" : "certificado-implantacion", isCert: true });
                  docs.push({ nombre: "Documento-implantacion.pdf", enlace: "documentacion-implantacion", hasContent: true });
                  if (detailLopd?.surveillanceVideo) {
                    docs.push({ nombre: "Video-vigilancia.pdf", enlace: "video-vigilancia" });
                  }
                  if (isAudit) {
                    docs.push({ nombre: "Documentación-auditoria.pdf", enlace: "documentacion-auditoria", hasContent: true });
                  }
                } else if (servpId === 5) {
                  docs.push({ nombre: "LOPD.pdf", enlace: "lopd", isFile: true });
                }

                const getDocUrl = (doc) => {
                  if (doc.isFile) {
                    const ruta = sale.docsProps?.find(d => d.type === "LOPD")?.ruta;
                    return ruta ? process.env.REACT_APP_STORAGE_URL + ruta : null;
                  }
                  if (detailLopd?.pendiente || detailLopd?.visitada) {
                    return sale.workcenters === null
                      ? `/doc-render/${sale.properties?.nif}/${sale.id}/${doc.enlace}`
                      : `/doc-render/${sale.properties?.nif}/${sale.workcenters.id}/${sale.id}/${doc.enlace}`;
                  }
                  return null;
                };

                return (
                  <div key={sale.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                    {/* Header */}
                    <div className="px-4 pt-4 pb-3 border-b border-gray-100">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-md font-semibold text-gray-800">
                          {sale?.serviceprops?.nombre || 'LOPD'} {getSaleYear(sale)}
                        </span>
                        {estadoId != null && (
                          <Badge label={estadoServicio(estadoId, t)} color={getEstadoBadgeColor(estadoId)} />
                        )}
                      </div>
                      {sale?.saleDate && (
                        <span className="text-xs text-gray-900">Fecha renovación: {formatoFechasEs(sale.saleDate)}</span>
                      )}
                    </div>

                    {/* Body: documentos como cards */}
                    <div className="p-4">
                      <p className="text-xs font-semibold text-gray-500 mb-3">{t("tabs.documents")}</p>
                      {docs.length === 0 ? (
                        <p className="text-sm text-gray-400">{t('details.noComunityService', { service: "LOPD" })}</p>
                      ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {docs.map((doc) => (
                            <LopdDocCard
                              key={doc.enlace}
                              nombre={doc.nombre}
                              url={getDocUrl(doc)}
                              badge={doc.isCert && detailLopd?.visitada ? "Favorable" : null}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              </div>
            ) : (
              <div className="bg-white border border-gray-200 rounded-xl px-5 py-8 text-center">
                <p className="text-sm text-gray-400">{t('details.noComunityService', { service: "LOPD" })}</p>
              </div>
            )}
          </div>
        )}

        {/* ═══ CAI TAB ═══ */}
        {activeTab === 3 && (
          <div className="space-y-4 max-w-8xl">
            <span className="text-md font-semibold text-gray-800">{t('details.comunityService', { service: "CAI" })}</span>
            {caiSales.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 items-start">
                {caiSales.map((sale) => (
                  <CaiSaleCard key={sale.id} sale={sale} t={t} />
                ))}
              </div>
            ) : (
              <div className="bg-white border border-gray-200 rounded-xl px-5 py-8 text-center">
                <p className="text-sm text-gray-400">{t('details.noComunityService', { service: "CAI" })}</p>
              </div>
            )}
          </div>
        )}

        {/* ═══ KAE TAB ═══ */}
        {activeTab === 4 && (
          <div className="space-y-4 max-w-8xl">
            <span className="text-md font-semibold text-gray-800">
              {t('details.comunityService', { service: "KAE" })}
            </span>
            {kaeInstalaciones.length > 0 ? (
              kaeInstalaciones.map((inst) => (
                <KaeInstalacionCard key={inst.id} instalacion={inst} t={t} />
              ))
            ) : (
              <div className="bg-white border border-gray-200 rounded-xl px-5 py-8 text-center">
                <p className="text-sm text-gray-400">
                  {t('details.noComunityService', { service: "KAE" })}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Comunidad;
